import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

/**
 * Post-approval consent modal. Shown automatically the first time a member
 * lands in the app after admin approval. Must accept Terms + Privacy to
 * dismiss. Optional marketing/partner toggles.
 */
export default function ConsentModal({ open, onComplete }) {
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [partnerComms, setPartnerComms] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset state whenever modal re-opens (defensive — modal only ever opens once)
  useEffect(() => {
    if (open) {
      setTerms(false); setPrivacy(false); setMarketing(false); setPartnerComms(false);
    }
  }, [open]);

  const canSubmit = terms && privacy;

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Please accept the Terms of Use and Privacy Policy to continue.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/consent/accept`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terms_accepted: terms,
          privacy_accepted: privacy,
          marketing_opt_in: marketing,
          partner_comms_opt_in: partnerComms,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      toast.success('Welcome to the SLL BriteHub community!');
      onComplete && onComplete();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissible until both required boxes are ticked */ }}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="consent-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-brand-navy/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-brand-navy" />
            </div>
            <div>
              <DialogTitle>One last step — confirm your preferences</DialogTitle>
              <p className="text-xs text-slate-500 mt-1">Welcome to SLL BriteHub. Before you start, please confirm the following.</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Required</p>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid="consent-terms-row">
              <Checkbox checked={terms} onCheckedChange={setTerms} className="mt-0.5" data-testid="consent-terms-check" />
              <span className="text-sm text-slate-700 leading-relaxed">
                I agree to the{' '}
                <a href="/governance/terms-of-use" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-navy hover:underline inline-flex items-center gap-0.5">
                  Terms of Use <ExternalLink className="h-3 w-3" />
                </a>
                {' '}and{' '}
                <a href="/governance/community-guidelines" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-navy hover:underline inline-flex items-center gap-0.5">
                  Community Guidelines <ExternalLink className="h-3 w-3" />
                </a>
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer mt-2" data-testid="consent-privacy-row">
              <Checkbox checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5" data-testid="consent-privacy-check" />
              <span className="text-sm text-slate-700 leading-relaxed">
                I have read and understood the{' '}
                <a href="/governance/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-navy hover:underline inline-flex items-center gap-0.5">
                  Privacy Policy <ExternalLink className="h-3 w-3" />
                </a>
              </span>
            </label>
          </div>

          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Optional</p>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid="consent-marketing-row">
              <Checkbox checked={marketing} onCheckedChange={setMarketing} className="mt-0.5" data-testid="consent-marketing-check" />
              <span className="text-sm text-slate-700 leading-relaxed">
                Send me updates and marketing from SLL BriteHub Community / Sustainable Logistics LIVE
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer mt-2" data-testid="consent-partner-row">
              <Checkbox checked={partnerComms} onCheckedChange={setPartnerComms} className="mt-0.5" data-testid="consent-partner-check" />
              <span className="text-sm text-slate-700 leading-relaxed">
                Share relevant opportunities and communications from selected partners and sponsors
              </span>
            </label>
          </div>

          <Button onClick={handleSubmit} disabled={!canSubmit || saving}
            className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white h-11"
            data-testid="consent-submit-btn">
            {saving ? 'Saving…' : 'Continue to SLL BriteHub'}
          </Button>
          <p className="text-[10px] text-slate-400 text-center">
            You can change your marketing preferences any time in your profile settings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
